import React from 'react';
import { Navigate } from 'react-router-dom';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useAuth } from '../contexts/AuthContext';

interface SubscriptionCheckProps {
  children: React.ReactNode;
  redirectTo?: string;
}

const SubscriptionCheck: React.FC<SubscriptionCheckProps> = ({ 
  children, 
  redirectTo = '/pricing' 
}) => {
  const { hasActiveSubscription, loading: subscriptionLoading, subscription } = useSubscription();
  const { loading: authLoading, user } = useAuth();

  console.log('SubscriptionCheck - authLoading:', authLoading, 'user:', user?.id, 'subscriptionLoading:', subscriptionLoading, 'hasActiveSubscription:', hasActiveSubscription, 'subscriptionStatus:', subscription?.status);

  // Show loading if auth is still loading
  if (authLoading) {
    console.log('SubscriptionCheck - Auth is loading, showing loading screen');
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading authentication...</p>
        </div>
      </div>
    );
  }

  // If user is not authenticated, redirect to login
  if (!user) {
    console.log('SubscriptionCheck - No user, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  // Show loading if subscription is still being checked
  if (subscriptionLoading) {
    console.log('SubscriptionCheck - Subscription is loading, showing loading screen');
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Checking subscription...</p>
        </div>
      </div>
    );
  }

  // Handle different subscription states
  if (subscription) {
    switch (subscription.status) {
      case 'active':
        console.log('SubscriptionCheck - User has active subscription, rendering children');
        return <>{children}</>;
        
      case 'past_due':
        console.log('SubscriptionCheck - Subscription is past due, allowing access with warning');
        return <>{children}</>;
        
      case 'unpaid':
        console.log('SubscriptionCheck - Subscription is unpaid, redirecting to subscription page');
        return <Navigate to="/subscription" replace />;
        
      case 'canceled':
        console.log('SubscriptionCheck - Subscription is canceled, redirecting to pricing');
        return <Navigate to={redirectTo} replace />;
        
      default:
        console.log('SubscriptionCheck - Unknown subscription status, redirecting to pricing');
        return <Navigate to={redirectTo} replace />;
    }
  }

  // If user is authenticated but has no active subscription, redirect to pricing
  if (!hasActiveSubscription) {
    console.log('SubscriptionCheck - No active subscription, redirecting to pricing');
    return <Navigate to={redirectTo} replace />;
  }

  console.log('SubscriptionCheck - User authenticated and has active subscription, rendering children');
  return <>{children}</>;
};

export default SubscriptionCheck; 